import React, { FormEvent } from 'react';

import axios from 'helpers/axios';
import config from 'helpers/config';
import PartsMatrix from 'models/PartsMatrixModel';
import { FaEdit, FaPlusCircle, FaRegTrashAlt } from 'react-icons/fa';
import Price from 'components/common/Price';
import PartsMatrixRange from 'models/PartsMatrixRangeModel';
import Toggle from 'components/common/Toggle';
import Percentage from 'components/common/Percentage';
import PriceInputCents from 'components/common/PriceInputCents';
import { connect } from 'react-redux';
import { getPricingMatrices } from 'actions/tenantActions';
import { Dispatch } from 'redux';
import { AlertTriangle, Copy, MoreHorizontal, Plus, Star, Trash2 } from 'lucide-react';

interface Props {
    dispatch: Dispatch;
}

interface State {
    matrices: PartsMatrix[];
    isCreateNewMatrixRangeActive: boolean;
    isNewMatrixBeingCreated: boolean;

    editableMatrixRanges: PartsMatrixRange[];
    editableMatrix: PartsMatrix | null;

    existingPartsMatrices: PartsMatrix[];

    editableRateId: string | null;
    name: string | null;
    rate: number | null;
}

class PartsMatricesSettings extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            matrices: [],
            existingPartsMatrices: [],
            isCreateNewMatrixRangeActive: false,
            editableMatrixRanges: [],
            isNewMatrixBeingCreated: false,
            editableRateId: null,
            editableMatrix: null,
            rate: null,
            name: null,
        };

        this.createNewMatrixRange = this.createNewMatrixRange.bind(this);
        this.updateMatrixRange = this.updateMatrixRange.bind(this);
    }

    componentDidMount() {
        this.getMatrices();
    }

    getMatrices() {
        axios.get(`${config.apiUrl}/partsMatrix`).then((res) => {
            this.setState({ existingPartsMatrices: res.data });
            this.props.dispatch<any>(getPricingMatrices());
        });
    }

    createNewMatrixRange() {
        const { name, rate } = this.state;
        const newMatrixRange = {
            name,
            rate,
        };

        axios.post(`${config.apiUrl}/partsMatrix`, newMatrixRange).then((_) => {
            this.getMatrices();
            this.setState({ name: null, rate: null, isCreateNewMatrixRangeActive: false });
        });
    }

    updateMatrixRange() {
        const { name, rate, editableRateId } = this.state;
        const matrixRange = {
            name,
            rate,
            id: editableRateId,
        };

        axios.put(`${config.apiUrl}/partsMatrix`, matrixRange).then((_) => {
            this.setState(
                { name: null, rate: null, isCreateNewMatrixRangeActive: false, editableRateId: null },
                this.getMatrices,
            );
        });
    }

    initNewMatrixRegistration() {
        this.setState({ isNewMatrixBeingCreated: true });

        const editableMatrixRanges = [
            {
                fromCents: 0,
                toCents: 1000,
                markup: 75,
            },
            {
                fromCents: 1001,
                toCents: 10000,
                markup: 20,
            },
        ] as PartsMatrixRange[];

        const newMatrix = {
            name: '',
            ranges: editableMatrixRanges,
        } as PartsMatrix;

        this.setState({ editableMatrixRanges, isNewMatrixBeingCreated: true, editableMatrix: newMatrix });
    }

    addNewRange() {
        const currentMatrix = this.state.editableMatrixRanges;
        const currentLastRange = currentMatrix[currentMatrix.length - 1];
        let lastRange = Number(String(currentMatrix[currentMatrix.length - 1].toCents).replace(/,/g, ''));
        if (lastRange <= 0 || isNaN(lastRange)) {
            // alert('Please fill in the last range ' + lastRange + ' ' + currentMatrix[currentMatrix.length - 1]);
            currentMatrix[currentMatrix.length - 1].toCents = Math.round(currentLastRange.fromCents + 100);
            lastRange = Math.round(currentLastRange.fromCents + 100);
        }

        const newRange = {
            fromCents: lastRange + 1,
            toCents: lastRange + 10000,
            markup: 0,
        } as PartsMatrixRange;

        // range.to = Number(String(range.to).replace(/,/g, ''));

        currentMatrix.push(newRange);

        this.setState({
            editableMatrixRanges: currentMatrix,
        });

        setTimeout(() => {
            const element = document.getElementById('price' + (currentMatrix.length - 1).toString() + 'to');
            element?.focus();
        }, 100);
    }

    updateRangeTo(newValue: number, index: number) {
        const currentMatrix = this.state.editableMatrixRanges;
        currentMatrix[index].toCents = newValue;
        this.setState({ editableMatrixRanges: currentMatrix });
    }

    handleSave() {
        const { editableMatrix, editableMatrixRanges } = this.state;
        editableMatrixRanges.forEach((range) => {
            range.toCents = Number(String(range.toCents).replace(/,/g, ''));
        });
        editableMatrix!.ranges = editableMatrixRanges;

        if (this.state.isNewMatrixBeingCreated) {
            axios.post(`${config.apiUrl}/partsMatrix`, editableMatrix).then((_) => this.getMatrices());
        } else {
            axios.put(`${config.apiUrl}/partsMatrix/`, editableMatrix).then((_) => this.getMatrices());
        }
    }

    isValidRange(priceFrom: number, priceToString: string) {
        const priceTo = Number(priceToString.replace(/,/g, ''));
        return !(priceFrom < 0 || priceTo <= 0 || isNaN(priceFrom) || isNaN(priceTo) || priceFrom >= priceTo);
    }

    handleDelete(range: PartsMatrixRange) {
        const { editableMatrixRanges } = this.state;
        axios.delete(`${config.apiUrl}/partsMatrix/range/${range.id}`).then((_) => {
            this.setState({ editableMatrixRanges: editableMatrixRanges.filter((r) => r !== range) });
        });
    }

    render() {
        const { matrices, isNewMatrixBeingCreated, editableMatrixRanges, editableMatrix, existingPartsMatrices } =
            this.state;
        return (
            <div className="flex flex-col ">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                                <span>Settings</span>
                                <span>/</span>
                                <span className="text-blue-600 font-semibold ">Pricing Matrices</span>
                            </nav>
                            <h2 className="text-3xl font-bold text-gray-900">Pricing Matrices</h2>
                            <p className="text-gray-600 mt-1">Configure markup percentages for different cost ranges</p>
                        </div>
                        <button
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                            onClick={() => this.initNewMatrixRegistration()}
                        >
                            <Plus className="h-4 w-4" />
                            <span>New Matrix</span>
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-8 p-8">
                    {/* New version */}
                    <div className="col-span-4">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                            <div className="p-6 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900">All Matrices</h3>
                                <p className="text-sm text-gray-600 mt-1">Select a matrix to edit</p>
                            </div>
                            <div className="p-4">
                                <div className="space-y-2">
                                    {existingPartsMatrices.map((matrix) => (
                                        <button
                                            key={matrix.id}
                                            onClick={() => {
                                                this.setState({
                                                    editableMatrixRanges: matrix.ranges,
                                                    isNewMatrixBeingCreated: false,
                                                    editableMatrix: matrix,
                                                });
                                            }}
                                            className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${
                                                editableMatrix?.id === matrix.id
                                                    ? 'bg-blue-50 border border-blue-200'
                                                    : 'hover:bg-gray-50 border border-transparent'
                                            }`}
                                        >
                                            <div className="flex items-center space-x-3">
                                                <div>
                                                    <div className="font-semibold text-gray-900">{matrix.name}</div>
                                                </div>
                                                <div className="flex-shrink-0">
                                                    {matrix.isDefault && (
                                                        <div className="flex items-center space-x-1">
                                                            <Star className="h-4 w-4 text-amber-500 fill-current" />
                                                            <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                                                                Default
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <button className="p-1 hover:bg-gray-100 rounded-md transition-colors">
                                                    <Copy className="h-4 w-4 text-gray-400" />
                                                </button>
                                                <button className="p-1 hover:bg-gray-100 rounded-md transition-colors">
                                                    <MoreHorizontal className="h-4 w-4 text-gray-400" />
                                                </button>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Old version */}
                    {/* <div className="mr-12 w-1/4 bg-gray-100 rouded-lg border p-2.5 rounded-lg h-fit">
                        <div className="pb-1.5 border-b-2 border-b-blue-500">
                            <span className="font-semibold text-gray-700">All Matrices:</span>
                        </div>
                        {existingPartsMatrices.map((matrix, index) => (
                            <div
                                key={index}
                                className={` border-b hover:border-b-gray-200 border-b-gray-100 py-2.5 clickable hover:bg-blue-100 pl-2.5 ${
                                    editableMatrix?.id === matrix.id ? 'bg-blue-100' : 'bg-white'
                                }`}
                                onClick={() => {
                                    this.setState({
                                        editableMatrixRanges: matrix.ranges,
                                        isNewMatrixBeingCreated: false,
                                        editableMatrix: matrix,
                                    });
                                }}
                            >
                                <div className="flex justify-between">
                                    <div className="font-semibold">{matrix.name}</div>
                                    <div className="flex">
                                        {matrix.isDefault && (
                                            <span
                                                style={{ backgroundColor: 'red' }}
                                                className="mr-2 px-1 py-1 rounded-lg hover:opacity-80 text-xs"
                                            >
                                                <span className="font-semibold text-white inline-flex whitespace-nowrap">
                                                    Default{' '}
                                                </span>
                                            </span>
                                        )}
                                        <FaEdit
                                            className="clickable mr-2"
                                            onClick={() => {
                                                this.setState({
                                                    editableMatrixRanges: matrix.ranges,
                                                    isNewMatrixBeingCreated: false,
                                                    editableMatrix: matrix,
                                                });
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div> */}
                    {/*  */}

                    <div className="col-span-8">
                        {!!(isNewMatrixBeingCreated || !!editableMatrix) && (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                                <div className="p-6 border-b border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                Matrix Configuration
                                            </h3>
                                            <input
                                                type="text"
                                                autoFocus
                                                value={editableMatrix?.name}
                                                onChange={(e) => {
                                                    const newName = e.target?.value;
                                                    this.setState((prevState) => ({
                                                        editableMatrix: {
                                                            ...prevState.editableMatrix!,
                                                            name: newName,
                                                        },
                                                    }));
                                                }}
                                                name="name"
                                                className="mt-2 text-xl font-semibold text-gray-900 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                                            />
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            <div className="flex items-center space-x-2">
                                                <span className="text-sm text-gray-600">Is default matrix</span>
                                                <button
                                                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 ${
                                                        editableMatrix?.isDefault
                                                            ? 'focus:ring-blue-500 focus:ring-offset-2 bg-blue-600'
                                                            : 'bg-gray-200'
                                                    }`}
                                                    onClick={() => {
                                                        this.setState((prevState) => ({
                                                            editableMatrix: {
                                                                ...prevState.editableMatrix!,
                                                                isDefault: !prevState.editableMatrix!.isDefault,
                                                            },
                                                        }));
                                                    }}
                                                >
                                                    <span
                                                        className={` pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                            editableMatrix?.isDefault
                                                                ? 'translate-x-5'
                                                                : 'translate-x-0'
                                                        }`}
                                                    ></span>
                                                </button>
                                            </div>

                                            <button
                                                className={`disabled:opacity-50 disabled:bg-green-500 disabled:cursor-not-allowed bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors`}
                                                disabled={editableMatrixRanges.some(
                                                    (range) =>
                                                        !this.isValidRange(range.fromCents, String(range.toCents)),
                                                )}
                                                onClick={() => this.handleSave()}
                                            >
                                                Save
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/*  */}
                                <div className="p-6">
                                    {/* <div className="pb-2">
                                        <label className="font-semibold">Matrix name</label>
                                        <input
                                            type="text"
                                            name="name"
                                            className="bg-gray-50 rounded-md border w-full px-2 text-lg font-semibold py-2 focus:ring-1 focus:outline-none focus:border-blue-500"
                                            autoFocus
                                            value={editableMatrix?.name}
                                            onChange={(e) => {
                                                const newName = e.target?.value;
                                                this.setState((prevState) => ({
                                                    editableMatrix: {
                                                        ...prevState.editableMatrix!,
                                                        name: newName,
                                                    },
                                                }));
                                            }}
                                        />
                                    </div> */}
                                    <div className="mb-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <h4 className="text-lg font-semibold text-gray-900">Pricing Ranges</h4>
                                            <button
                                                onClick={() => this.addNewRange()}
                                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-semibold flex items-center space-x-2 transition-colors"
                                            >
                                                <Plus className="h-4 w-4" />
                                                <span>Add Range</span>
                                            </button>
                                        </div>

                                        <div className="overflow-hidden rounded-lg border border-gray-200">
                                            <table className="w-full">
                                                <thead className="bg-blue-600 text-white">
                                                    <tr className="bg-blue-500 h-12 text-white border-none font-semibold text-left text-xs uppercase align-middle">
                                                        <th className="px-6 py-4 text-left text-sm font-semibold">
                                                            Cost From
                                                        </th>
                                                        <th className="px-6 py-4 text-left text-sm font-semibold">
                                                            Cost To
                                                        </th>
                                                        <th className="px-6 py-4 text-left text-sm font-semibold">
                                                            Markup
                                                        </th>
                                                        <th className="px-6 py-4 text-left text-sm font-semibold">
                                                            Gross profit
                                                        </th>
                                                        <th className="px-6 py-4 text-left text-sm font-semibold"></th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200">
                                                    {editableMatrixRanges.map((range, index) => {
                                                        return (
                                                            <tr className={`hover:bg-gray-50`} key={index}>
                                                                <td className="px-6 py-4">
                                                                    <div className="whitespace-pre-wrap">
                                                                        <Price
                                                                            price={range.fromCents}
                                                                            isInCents={true}
                                                                        />
                                                                    </div>
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <div className="whitespace-pre-wrap">
                                                                        <span>
                                                                            {/* <span className="font-semibold inline">$</span> */}
                                                                            {/* <PriceInput
                                                                        placeholder="0.00"
                                                                        name="price"
                                                                        onChange={(e) => this.updateRange(e, index)}
                                                                        amount={range.toCents / 100}
                                                                        id={'price' + index.toString() + 'to'}
                                                                        className={`outline-none w-24 placeholder-gray-400 font-semibold px-3 py-2 ${
                                                                            this.isValidRange(
                                                                                range.fromCents,
                                                                                String(range.toCents),
                                                                            )
                                                                                ? 'focus:ring-blue-600 focus:ring-1'
                                                                                : 'focus:ring-red-600 focus:ring-1'
                                                                        }  rounded-lg text-right `}
                                                                        hasError={
                                                                            !this.isValidRange(
                                                                                range.fromCents,
                                                                                String(range.toCents),
                                                                            )
                                                                        }
                                                                    /> */}
                                                                            <PriceInputCents
                                                                                initialPriceCents={range.toCents}
                                                                                handlePriceChange={(priceCents) => {
                                                                                    this.updateRangeTo(
                                                                                        priceCents,
                                                                                        index,
                                                                                    );
                                                                                }}
                                                                                className={`outline-none w-24 placeholder-gray-400 font-semibold px-3 py-2 ${
                                                                                    this.isValidRange(
                                                                                        range.fromCents,
                                                                                        String(range.toCents),
                                                                                    )
                                                                                        ? 'focus:ring-blue-600 focus:ring-1'
                                                                                        : 'focus:ring-red-600 focus:ring-1'
                                                                                }  rounded-lg text-right `}
                                                                                hasError={
                                                                                    !this.isValidRange(
                                                                                        range.fromCents,
                                                                                        String(range.toCents),
                                                                                    )
                                                                                }
                                                                            />
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="px-6 py-4 non-clickable">
                                                                    <div className="flex ">
                                                                        <input
                                                                            className={`outline-none w-16 placeholder-gray-400 font-semibold px-0 pl-0.5 py-2 ${
                                                                                range.markup < 0
                                                                                    ? 'focus:ring-red-600 focus:ring-2'
                                                                                    : 'focus:ring-blue-600 focus:ring-1'
                                                                            }  rounded-lg text-right `}
                                                                            type="number"
                                                                            min={0}
                                                                            value={range?.markup || 0}
                                                                            id={'markup' + index.toString()}
                                                                            onChange={(e) => {
                                                                                const { value } = e.target;
                                                                                this.setState((prevState) => {
                                                                                    const newRanges = [
                                                                                        ...prevState.editableMatrixRanges,
                                                                                    ];
                                                                                    newRanges[index].markup =
                                                                                        Number(value);
                                                                                    return {
                                                                                        editableMatrixRanges: newRanges,
                                                                                    };
                                                                                });
                                                                            }}
                                                                        />
                                                                        <span className="font-semibold inline pt-1.5">
                                                                            %
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <div className="flex ">
                                                                        <Percentage
                                                                            percentage={
                                                                                (1 * (1 + range.markup / 100) - 1) /
                                                                                (1 * (1 + range.markup / 100))
                                                                            }
                                                                        />
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    {/* <FaRegTrashAlt
                                                                        size={15}
                                                                        className="inline clickable mr-1"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            this.handleDelete(range);
                                                                        }}
                                                                    /> */}

                                                                    <button
                                                                        className="p-1 text-gray-500 hover:text-red-600"
                                                                        title="Delete Group Task"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            this.handleDelete(range);
                                                                        }}
                                                                    >
                                                                        <Trash2 size={18} />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                        <div className="flex items-start space-x-3">
                                            <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <h5 className="font-semibold text-blue-900">
                                                    How pricing matrices work
                                                </h5>
                                                <p className="text-sm text-blue-800 mt-1">
                                                    When creating repair orders, the system will automatically apply the
                                                    appropriate markup percentage based on the cost range. Gross profit
                                                    is calculated as: (Markup ÷ (100 + Markup)) × 100
                                                </p>
                                                <ul className="text-sm text-blue-800 mt-2 space-y-1">
                                                    <li>• Ranges should not overlap</li>
                                                    <li>
                                                        • Default matrix is used when no specific matrix is assigned
                                                    </li>
                                                    <li>
                                                        • Higher cost ranges typically have lower markup percentages
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Add new range button */}
                                    {/* <div className="mt-4 flex">
                                        <button
                                            className="bg-blue-500 px-2 py-1.5 rounded-2xl font-semibold text-base"
                                            onClick={() => this.addNewRange()}
                                        >
                                            <FaPlusCircle className="inline mr-2 align-baseline" />

                                            <span>Add range</span>
                                        </button>

                                        <div className="ml-auto">
                                            <div className="flex">
                                                <div className="mr-2">
                                                    <Toggle
                                                        onChange={(newValue) =>
                                                            this.setState((prevState) => ({
                                                                editableMatrix: {
                                                                    ...prevState.editableMatrix!,
                                                                    isDefault: newValue,
                                                                },
                                                            }))
                                                        }
                                                        name="isDefaultMatrix"
                                                        label="Is default matrix"
                                                        value={editableMatrix?.isDefault || false}
                                                        isLabelLeft={true}
                                                        disabled={existingPartsMatrices.some(
                                                            (matrix) =>
                                                                matrix.isDefault && matrix.id !== editableMatrix?.id,
                                                        )}
                                                    />
                                                </div>
                                                <button
                                                    className="bg-green-500 px-2 py-1.5 rounded-2xl font-semibold text-base
                                                    disabled:opacity-50 disabled:bg-green-500 disabled:cursor-not-allowed
                                                    "
                                                    disabled={editableMatrixRanges.some(
                                                        (range) =>
                                                            !this.isValidRange(range.fromCents, String(range.toCents)),
                                                    )}
                                                    onClick={() => this.handleSave()}
                                                >
                                                    Save
                                                </button>
                                            </div>
                                        </div>
                                    </div> */}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default connect()(PartsMatricesSettings);
